import React, {useContext} from "react";
import {Box, ListItemButton, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {RAIL, rgba} from "../rail/railTokens";
import {SideSectionContext, useSideMenuStyle} from "../sideMenuStyle";

// Registry projects: a row of the white list panel with the section icon, like the supervision list
const PanelRow = ({label, onClick, active, action}) => {
    const section = useContext(SideSectionContext) || {color: "#8491F7"};
    return (
        <ListItemButton
            disableRipple
            onClick={onClick}
            sx={{
                minHeight: 0, gap: 1.25, px: 1.25, py: 0.875, my: 0.25, borderRadius: "12px",
                bgcolor: active ? "rgba(132, 145, 247, 0.12)" : "transparent",
                "&:hover": {bgcolor: active ? "rgba(132, 145, 247, 0.16)" : "#F6F6FB"},
                "&:hover .side-row-action": {opacity: 1},
            }}
        >
            <Box sx={{
                width: 28, height: 28, borderRadius: "9px", flexShrink: 0, display: "flex", alignItems: "center",
                justifyContent: "center", color: section.color, bgcolor: `${section.color}1F`,
            }}>
                {section.icon && <HugeiconsIcon icon={section.icon} size={14}/>}
            </Box>
            <Typography noWrap sx={{
                flex: 1, minWidth: 0, fontSize: 13, fontWeight: active ? 700 : 600, color: active ? "#4B55C9" : "#23264D",
            }}>
                {label}
            </Typography>
            {action && (
                <Box className="side-row-action"
                     sx={{display: "flex", alignItems: "center", opacity: active ? 1 : 0, transition: "opacity .12s ease", color: "#8A8AA0"}}>
                    {action}
                </Box>
            )}
        </ListItemButton>
    );
};

/**
 * One row inside a {@link ListBlock}.
 *
 * - `label`    text shown in the row
 * - `onClick`  row click handler
 * - `active`   selected look (accent pill)
 * - `action`   optional trailing element (e.g. an edit icon button); it is
 *              revealed on row hover. Stop propagation in its handler if it
 *              should not also trigger `onClick`.
 * - `tooltip`  optional tooltip text (handy for truncated names)
 */
export const SideItemRow = ({label, onClick, active = false, action = null, tooltip}) => {
    const {variant} = useSideMenuStyle();
    const row = variant === "panel" ? <PanelRow label={label} onClick={onClick} active={active} action={action}/> : (
        <ListItemButton
            disableRipple
            onClick={onClick}
            sx={{
                position: "relative",
                minHeight: 0,
                px: 1,
                py: 0.5,
                my: 0.15,
                borderRadius: "8px",
                color: active ? RAIL.accent : RAIL.muted,
                backgroundColor: active ? rgba(RAIL.accent, 0.12) : "transparent",
                transition: "background-color .12s ease, color .12s ease",
                "&:hover": {
                    backgroundColor: rgba(RAIL.accent, active ? 0.16 : 0.08),
                    color: active ? RAIL.accent : RAIL.ink,
                },
                "&:hover .side-row-action": {opacity: 1},
            }}
        >
            <Typography
                noWrap
                sx={{flex: 1, fontSize: 12.5, fontWeight: active ? 600 : 500, color: "inherit"}}
            >
                {label}
            </Typography>
            {action && (
                <Box
                    className="side-row-action"
                    sx={{display: "flex", alignItems: "center", ml: 0.5, opacity: 0, transition: "opacity .12s ease"}}
                >
                    {action}
                </Box>
            )}
        </ListItemButton>
    );

    return tooltip
        ? <Tooltip title={tooltip} placement="right" disableInteractive enterDelay={400}>{row}</Tooltip>
        : row;
};
